import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Alert,
  IconButton,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  PhotoCamera as CameraIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { notificationService } from '@/services/notificationService';

interface ReceiptUploadFormProps {
  file: File | undefined;
  onChange: (file: File | undefined) => void;
  onNext: () => void;
  onBack: () => void;
  disabled: boolean;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];

export const ReceiptUploadForm: React.FC<ReceiptUploadFormProps> = ({
  file,
  onChange,
  onNext,
  onBack,
  disabled,
}) => {
  const [preview, setPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (selectedFile: File) => {
    // Validazione tipo file
    if (!ALLOWED_TYPES.includes(selectedFile.type)) {
      notificationService.error(
        'Tipo file non supportato',
        'Sono supportati solo file PNG, JPG e PDF'
      );
      return;
    }

    // Validazione dimensione
    if (selectedFile.size > MAX_FILE_SIZE) {
      notificationService.error(
        'File troppo grande',
        'La dimensione massima è 10MB'
      );
      return;
    }

    onChange(selectedFile);

    // Crea preview per immagini
    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target?.result as string);
      };
      reader.readAsDataURL(selectedFile);
    } else {
      setPreview(null);
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const handleFileClick = () => {
    // Apre il menu del sistema con tutte le opzioni disponibili
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemoveFile = () => {
    onChange(undefined);
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) {
      return <ImageIcon />;
    }
    return <UploadIcon />;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Ricevuta (Opzionale)
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Carica una foto della ricevuta o del documento di donazione. 
        Sono supportati file PNG, JPG e PDF fino a 10MB.
      </Typography>

      {!file ? (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Carica Ricevuta
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Scegli come caricare la ricevuta
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<CameraIcon />}
                onClick={handleFileClick}
                disabled={disabled}
                size="large"
                sx={{ minWidth: 200 }}
              >
                Carica Foto/File
              </Button>
            </Box>

            <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }}>
              <Typography variant="body2">
                <strong>Suggerimenti per una buona foto:</strong>
                <br />
                • Assicurati che il testo sia leggibile
                <br />
                • Evita riflessi e ombre
                <br />
                • Mantieni la ricevuta piana
                <br />
                • Usa una buona illuminazione
                <br />
                <br />
                <strong>Come caricare:</strong> Premi il pulsante per aprire il menu con le opzioni: scatta una nuova foto, scegli dalla galleria o seleziona un file.
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      ) : (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {preview ? (
                  <Box
                    component="img"
                    src={preview}
                    alt="Preview"
                    sx={{
                      width: 60,
                      height: 60,
                      objectFit: 'cover',
                      borderRadius: 1,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                    }}
                  >
                    {getFileIcon(file)}
                  </Box>
                )}
                
                <Box>
                  <Typography variant="subtitle1" noWrap>
                    {file.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatFileSize(file.size)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disabled}
                  size="small"
                >
                  <UploadIcon />
                </IconButton>
                <IconButton
                  onClick={handleRemoveFile}
                  disabled={disabled}
                  size="small"
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Input nascosti */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_TYPES.join(',')}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          onClick={onBack}
          disabled={disabled}
          size="large"
        >
          Indietro
        </Button>
        <Button
          variant="contained"
          onClick={onNext}
          disabled={disabled}
          size="large"
        >
          Avanti
        </Button>
      </Box>
    </Box>
  );
};
